/**
 * @file audio/levelTap.js
 * @description The one analyser tap in the app. A read-only probe hung off any
 *              audio node that reports the level of what passes through it, so
 *              level-driven UI (the gain rail's meter, the event-shaped effect
 *              visuals, the dev harness) never has to build its own analyser.
 *
 *              Two detectors, because two things want different truths:
 *                - `rms`  — perceived loudness, what the effect visuals pulse on.
 *                - `peak` — the largest sample in the window, what a level meter
 *                  must show so a transient that would clip is actually visible.
 *
 *              `holdDecay` keeps a reading alive across frames so a short
 *              transient landing between two reads is not simply missed. Callers
 *              that own their own ballistics (a meter with a release curve) pass
 *              `holdDecay: 0` and get the raw per-window value.
 *
 *              Meter failures must never break the audio graph: construction
 *              returns null when the tap can't be wired, and `read()` never
 *              throws.
 */

/** Frame-to-frame decay of the held value (~0.88 ≈ readable for 2–3 frames at 60fps). */
const DEFAULT_HOLD_DECAY = 0.88;

/**
 * @param {BaseAudioContext} context - The audio context that owns `sourceNode`.
 * @param {object} sourceNode - Node carrying the signal to measure (native or
 *        Tone — anything with a `connect` accepting a native AnalyserNode).
 * @param {object} [options]
 * @param {'rms'|'peak'} [options.detector='rms'] - What `read()` measures.
 * @param {number} [options.holdDecay=0.88] - Per-read decay of the held value;
 *        `0` disables the hold and returns the raw window value.
 * @returns {{ read(): number, dispose(): void }|null} Null when the tap can't be
 *          created — callers treat a missing tap as level 0.
 */
export function createLevelTap(context, sourceNode, options = {}) {
  if (!context || typeof context.createAnalyser !== 'function' || !sourceNode) return null;

  const detector = options.detector === 'peak' ? 'peak' : 'rms';
  const holdDecay = Number.isFinite(options.holdDecay) ? options.holdDecay : DEFAULT_HOLD_DECAY;

  let analyser;
  try {
    analyser = context.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0;
    sourceNode.connect(analyser);
  } catch (_) {
    return null;
  }

  const samples = new Float32Array(analyser.fftSize);
  let held = 0;

  return {
    /** Level of the last analyser window, linear (0..1 for an unclipped signal, above 1 when hot). */
    read() {
      try {
        analyser.getFloatTimeDomainData(samples);
      } catch (_) {
        return 0;
      }
      let level = 0;
      if (detector === 'peak') {
        for (let i = 0; i < samples.length; i += 1) {
          const magnitude = Math.abs(samples[i]);
          if (magnitude > level) level = magnitude;
        }
      } else {
        let sum = 0;
        for (let i = 0; i < samples.length; i += 1) sum += samples[i] * samples[i];
        level = Math.sqrt(sum / samples.length);
      }
      held = Math.max(level, held * holdDecay);
      return held;
    },
    dispose() {
      try {
        sourceNode.disconnect(analyser);
      } catch (_) {}
    },
  };
}

export default createLevelTap;
